import { useEffect, useMemo, useState } from 'react';
import { GoogleMap, Polygon } from '@react-google-maps/api';

import CountryStatMarker from './CountryStatMarker';
import { countryCenter } from '../geo/countryCenter';
import { countryGeo10, Geometry } from '../geo/custom.geo.10';
import { useGoogleMapsContext } from '../../../../GoogleMapsProvider';
import Loading from '../../../Common/Loading';

const options = {
  disableDefaultUI: true,
  styles: [
    {
      elementType: 'geometry',
      stylers: [
        {
          color: '#212121',
        },
      ],
    },
    {
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575',
        },
      ],
    },
    {
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#212121',
        },
      ],
    },
    {
      featureType: 'administrative',
      elementType: 'geometry',
      stylers: [
        {
          color: '#757575',
        },
      ],
    },
    {
      featureType: 'administrative.country',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9e9e9e',
        },
      ],
    },
    {
      featureType: 'administrative.land_parcel',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'administrative.locality',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#bdbdbd',
        },
      ],
    },
    {
      featureType: 'administrative.neighborhood',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.text',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575',
        },
      ],
    },
    {
      featureType: 'poi.business',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry',
      stylers: [
        {
          color: '#181818',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#616161',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#1b1b1b',
        },
      ],
    },
    {
      featureType: 'road',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#2c2c2c',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'labels',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#8a8a8a',
        },
      ],
    },
    {
      featureType: 'road.arterial',
      elementType: 'geometry',
      stylers: [
        {
          color: '#373737',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [
        {
          color: '#3c3c3c',
        },
      ],
    },
    {
      featureType: 'road.highway.controlled_access',
      elementType: 'geometry',
      stylers: [
        {
          color: '#4e4e4e',
        },
      ],
    },
    {
      featureType: 'road.local',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#616161',
        },
      ],
    },
    {
      featureType: 'transit',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'transit',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [
        {
          color: '#000000',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#3d3d3d',
        },
      ],
    },
  ],
  maxZoom: 14,
  minZoom: 2,
  gestureHandling: 'cooperative',
};

const convertGeoJsonCoordinates = (
  geometry: Geometry,
): google.maps.LatLngLiteral[][] => {
  if (geometry.type === 'Polygon') {
    return geometry.coordinates.map((ring) => ring.map(([lng, lat]) => ({ lat, lng })));
  }

  if (geometry.type === 'MultiPolygon') {
    return geometry.coordinates.flatMap((polygon) => polygon.map((ring) => ring.map(([lng, lat]) => ({ lat, lng }))));
  }

  return [];
};

interface InternationalMapProps {
  center?: google.maps.LatLngLiteral;
  markers?: { countryCode: string; avarage: number; id: string }[];
  selectedCountryIso3?: string;
}

function InternationalMap({
  center = { lat: 46, lng: -8 },
  markers,
  selectedCountryIso3,
}: InternationalMapProps) {
  const { isLoaded } = useGoogleMapsContext();
  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);
  const selectedCountry = useMemo(
    () => countryGeo10.features.find(
      (f) => f.properties.iso3 === selectedCountryIso3,
    ),
    [selectedCountryIso3],
  );

  const getBoundsFromGeometry = (
    geometry: Geometry,
  ): google.maps.LatLngBounds => {
    const bounds = new window.google.maps.LatLngBounds();

    const coordsList = geometry.type === 'Polygon'
      ? geometry.coordinates
      : geometry.coordinates.flat();

    coordsList.forEach((ring) => {
      ring.forEach(([lng, lat]) => {
        bounds.extend({ lat, lng });
      });
    });

    return bounds;
  };

  useEffect(() => {
    if (selectedCountry && mapRef) {
      const bounds = getBoundsFromGeometry(selectedCountry.geometry);

      mapRef.fitBounds(bounds, {
        bottom: 5,
        left: 5,
        right: 5,
        top: 150,
      });
    }
  }, [selectedCountry, mapRef]);

  if (!isLoaded) {
    return <Loading absolute />;
  }

  return (
    <GoogleMap
      onLoad={(map) => setMapRef(map)}
      mapContainerStyle={{
        width: '100%',
        height: '700px',
      }}
      center={selectedCountryIso3 ? undefined : center}
      zoom={selectedCountryIso3 ? undefined : 3}
      options={options}
    >
      {markers?.map(({ countryCode, avarage, id }) => (
        <CountryStatMarker
          key={countryCode}
          position={countryCenter[countryCode]}
          value={avarage}
          internationalId={id}
        />
      ))}

      {selectedCountry
        && convertGeoJsonCoordinates(selectedCountry.geometry).map((path, i) => (
          <Polygon
            key={i}
            paths={path}
            options={{
              fillColor: 'green',
              fillOpacity: 0.4,
              strokeColor: 'green',
              strokeOpacity: 0.8,
              strokeWeight: 2,
            }}
          />
        ))}
    </GoogleMap>
  );
}

export default InternationalMap;
